import { Grid2, Typography, Box, Modal, Slide, } from "@mui/material"
import { memo, useMemo, useEffect, useState, useCallback } from "react";
import Table from "../../components/Table.js"
import {
    getOwners,
    getMatings,
    getAnimal
} from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import { useSnackbar } from "../../utils/index.js"
import Form from "../../components/Form.js";
import Spinner from "../../components/Spinner.js";
import {
    matingTypeHeader,
    matingFatherHeader,
    matingMotherHeader,
    matingDateHeader,
    sireInDateHeader,
    sireOutDateHeader,
} from "../../components/TableHeaders.js"
import AnimalForm from "../../components/forms/AnimalForm.js";

const sexTranslates = [
    { en: "male", gr: "Αρσενικά" },
    { en: "female", gr: "Θηλυκά" },
];

const MatingsList = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [ownerSexSelected, setOwnerSexSelected] = useState(false);
    const [fetchedMatings, setFetchedMatings] = useState([]);
    const [isUpdateOpen, setIsUpdateOpen] = useState(false);
    const [animalOwner, setAnimalOwner] = useState(null);
    const [animalOwnerId, setAnimalOwnerId] = useState(null);
    const [animalToUpdate, setAnimalToUpdate] = useState(null);
    const [selectedOwnerId, setSelectedOwnerId] = useState(null);

    const classes = useSelectStyles();
    const listClasses = useListStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));

            } catch (err) {
                error(err)
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const handleAnimalCodeClick = async (value) => {
        const { animal } = await getAnimal(value, selectedOwnerId);
        const animalToUpdate = { ...animal };
        const animalsOwner = fetchedOwners.find((owner) => owner._id === animalToUpdate.owner);
        animalsOwner ? animalToUpdate.owner = `${animalsOwner.lastName} ${animalsOwner.firstName}` : animalToUpdate.owner = null;
        setAnimalOwner(animalsOwner);
        setAnimalOwnerId(animalsOwner?._id);
        setAnimalToUpdate(animalToUpdate);
        setIsUpdateOpen(true);
    }

    const tableColumns = useMemo(() => [
        matingTypeHeader,
        matingFatherHeader(handleAnimalCodeClick),
        matingMotherHeader(handleAnimalCodeClick),
        matingDateHeader,
        sireInDateHeader,
        sireOutDateHeader,
    ]);

    const ownerSexForm = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "sex",
            items: [
                { value: "Αρσενικά", text: "Αρσενικά" },
                { value: "Θηλυκά", text: "Θηλυκά" },
            ],
            placeholder: "Φύλλο",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο",
        },
    ]

    const handleSelectOwnerSexSubmit = async (values) => {
        setIsLoading(true);
        const { owner, sex } = values;
        const ownerId = fetchedOwners.find((fetchedOwner) => `${fetchedOwner?.lastName} ${fetchedOwner?.firstName} - ${fetchedOwner?.code}` === owner)?._id;
        setSelectedOwnerId(ownerId);
        const sexToSend = sexTranslates.find((translate) => translate.gr === sex)?.en;
        const { success: scs, matings, message } = await getMatings(ownerId, sexToSend);
        if (scs) {
            setFetchedMatings(matings);
            setOwnerSexSelected(true);
        } else {
            error(message)
        }
        setIsLoading(false);
    }
    return (
        <>
            <Spinner open={isLoading} />
            {!ownerSexSelected && (
                <>
                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>

                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                Επιλογή Εκτροφής και Ζώων
                            </Typography>
                            <Form content={ownerSexForm} onSubmit={handleSelectOwnerSexSubmit} />

                        </Box>
                    </Box>
                </>
            )}
            {ownerSexSelected && (
                <>
                    <Modal open={isUpdateOpen} onClose={() => setIsUpdateOpen(false)} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Slide direction="left" in={isUpdateOpen} mountOnEnter unmountOnExit>
                            <Box className={listClasses.modalContent}>
                                <Typography variant="h5" className={listClasses.modal_title}>
                                    Επεξεργασία Ζώου
                                </Typography>
                                <AnimalForm
                                    datePickerWidth="100%"
                                    isCreate={false}
                                    isFromList={true}
                                    setIsCreateOpen={setIsUpdateOpen}
                                    fetchedOwner={animalOwner}
                                    setFetchedOwner={setAnimalToUpdate}
                                    ownerId={animalOwnerId}
                                    flockCode={animalToUpdate?.flockCode}
                                    fetchedAnimal={animalToUpdate}
                                    setFetchedAnimal={setAnimalToUpdate}
                                    fetchedOwners={fetchedOwners}
                                    ownersDropdownValues={ownersDropdownValues}
                                />
                            </Box>
                        </Slide>
                    </Modal>
                    <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                            <Typography variant="h5" className={listClasses.form_title}>
                                Ζυζεύξεις
                            </Typography>
                        </Box>
                        <Table
                            data={fetchedMatings}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν εγγραφές!"}
                            columns={tableColumns}
                            defaultSorted={[{ id: "matingDate1", desc: true }]}
                            dropdownOptions={[{ value: "Άρρεν", label: "Άρρεν" }, { value: "Θήλυ", label: "Θήλυ" }]}
                        />
                    </Grid2>
                </>
            )}
        </>
    )
}

export default memo(MatingsList);